import React from 'react';
import { PublicLayout } from '../../components/PublicLayout';
import { PageHero, FAQSection, CTASection } from '../../components/PageTemplate';
import { Check, X, Building2, Calendar, Shield, Eye, FileCheck } from 'lucide-react';
import { Link } from 'react-router-dom';

export default function LandlordsPricing() {
  const tiers = [
    {
      name: 'Fully Managed',
      price: '6%',
      priceDetail: 'of monthly rent',
      description: 'Complete hands-off property management',
      features: [
        'Property listing on all major portals',
        'Professional photography consultation',
        'Tenant referencing & verification',
        'Digital tenancy agreement',
        'E-signatures via DocuSign',
        'Deposit protection setup',
        'Rent collection via Direct Debit',
        'Arrears chasing & management',
        'Maintenance coordination',
        'Auto-approval for repairs under £200',
        'Quarterly property inspections',
        'Tenancy renewals',
        'End of tenancy management',
        'Deposit return handling',
        '24/7 emergency line',
        'Monthly financial statements',
        'Complete audit trail'
      ],
      notIncluded: [],
      cta: 'Get Started',
      popular: true,
      icon: Building2
    }
  ];

  const asAServiceModules = [
    {
      id: 'vaas',
      name: 'Viewings as a Service (VaaS)',
      price: '£250',
      priceDetail: 'per 6-hour block',
      description: 'Professional staff conduct property viewings on your behalf',
      icon: Eye,
      features: [
        'Professional viewing agents',
        'Flexible scheduling',
        'Feedback collection',
        'Lead qualification'
      ],
      popular: true
    },
    {
      id: 'caas',
      name: 'Compliance as a Service (CaaS)',
      price: '2%',
      priceDetail: 'recurring monthly',
      description: 'We manage all regulatory and legal compliance',
      icon: Shield,
      features: [
        'Gas Safety Certificate management',
        'EPC & EICR tracking',
        'Legionella risk assessments',
        'Automated renewal reminders'
      ],
      popular: true
    },
    {
      id: 'iaas',
      name: 'Inspections as a Service (IaaS)',
      price: '£50',
      priceDetail: 'per inspection',
      description: 'LiDAR-powered inspections with AI damage detection',
      icon: FileCheck,
      features: [
        'LiDAR 3D room scanning',
        'AI damage detection',
        'Move-in/move-out comparison',
        'Detailed PDF reports'
      ],
      note: 'For 12-month tenancies, we recommend 4 quarterly inspections.',
      popular: true
    }
  ];

  const inspectionPackages = [
    { name: 'Single Inspection', inspections: 1, price: 50, perInspection: 50, savings: null },
    { name: 'Bi-Annual Pack', inspections: 2, price: 95, perInspection: 47.50, savings: 5 },
    { name: 'Quarterly Pack', inspections: 4, price: 180, perInspection: 45, savings: 20, recommended: true }
  ];

  const additionalServices = [
    { name: 'Rent Protection Insurance', price: '£299/year', description: 'Up to 12 months rent coverage' },
    { name: 'Legal Expenses Insurance', price: '£199/tenancy', description: 'Eviction and legal costs cover' },
    { name: 'Home Emergency Cover', price: '£149/year', description: '24/7 emergency callout service' },
    { name: 'White Goods Cover', price: '£12.99/month', description: 'Repair/replacement for items ≤3 years old' }
  ];

  const platformFees = [
    { platform: 'Rightmove', price: '£50', description: "UK's #1 property portal" },
    { platform: 'Zoopla', price: '£40', description: 'Major property search engine' },
    { platform: 'OnTheMarket', price: '£35', description: 'Agent-backed portal' },
    { platform: 'SpareRoom', price: '£25', description: 'Room share platform' },
    { platform: 'Gumtree', price: '£20', description: 'Classifieds marketplace' }
  ];

  const faqs = [
    {
      question: 'What is the listing fee?',
      answer: 'The £50 listing fee is a one-time administrative charge per property. Platform-specific fees (Rightmove, Zoopla, etc.) are separate pass-through costs that you select based on where you want to advertise.'
    },
    {
      question: 'How does the 6% management fee work?',
      answer: 'The 6% management fee is charged on rent collected for fully managed properties. If your property is empty or no rent is collected, no fee is charged. It\'s automatically deducted before transferring rent to you.'
    },
    {
      question: 'What is Compliance as a Service?',
      answer: 'CaaS is a 2% recurring fee that covers complete compliance management - Gas Safety Certificates, EPCs, EICRs, and all regulatory requirements. We handle renewals, reminders, and contractor scheduling so you never miss a deadline.'
    },
    {
      question: 'How does Inspections as a Service work?',
      answer: 'Each inspection costs £50 and includes LiDAR 3D scanning plus AI-powered damage detection. For 12-month tenancies, we recommend the Quarterly Pack (4 inspections for £180) which can be paid upfront or deducted from rent.'
    },
    {
      question: 'Can I choose which services I need?',
      answer: 'Absolutely. Our modular approach means you only pay for what you need. Start with Fully Managed at 6%, then layer on VaaS, CaaS, or IaaS as needed.'
    }
  ];

  return (
    <PublicLayout>
      <PageHero
        badge="TRANSPARENT PRICING"
        title="Simple, Modular"
        subtitle="Pricing"
        description="Pay only for what you need. Our modular approach means no hidden fees - just straightforward pricing that scales with your portfolio."
        gradient="from-emerald-50 via-white to-slate-50"
      />

      {/* Service Tiers */}
      <section className="py-12 sm:py-16 lg:py-20 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-10">
            <h2 className="text-2xl sm:text-3xl font-display font-bold mb-3">Our Service</h2>
            <p className="text-slate-600">Complete property management with transparent pricing</p>
          </div>
          
          <div className="max-w-lg mx-auto">
            {tiers.map((tier) => (
              <div
                key={tier.name}
                className={`relative rounded-2xl p-6 sm:p-8 border-2 ${
                  tier.popular 
                    ? 'border-primary shadow-xl' 
                    : 'border-slate-200'
                }`}
              >
                {tier.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 bg-primary text-white text-sm font-semibold rounded-full">
                    Most Popular
                  </div>
                )}
                <div className="flex items-center gap-3 mb-4">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${tier.popular ? 'bg-primary/10 text-primary' : 'bg-slate-100 text-slate-600'}`}>
                    <tier.icon className="w-5 h-5" />
                  </div>
                  <h3 className="text-xl sm:text-2xl font-bold">{tier.name}</h3>
                </div>
                <div className="flex items-baseline gap-1 mb-2">
                  <span className="text-3xl sm:text-4xl font-extrabold text-primary">{tier.price}</span>
                  {tier.priceDetail && (
                    <span className="text-slate-500 text-sm">{tier.priceDetail}</span>
                  )}
                </div>
                <p className="text-slate-600 mb-6">{tier.description}</p>
                
                <ul className="space-y-3 mb-6">
                  {tier.features.map((feature, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                      <span className="text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>

                {tier.notIncluded.length > 0 && (
                  <div className="mb-6 pt-4 border-t border-slate-100">
                    <p className="text-sm text-slate-500 mb-2">Not included:</p>
                    <ul className="space-y-1">
                      {tier.notIncluded.map((item, i) => (
                        <li key={i} className="flex items-center gap-2 text-sm text-slate-400">
                          <X className="w-4 h-4" />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                <Link
                  to="/register"
                  className={`block w-full py-3 text-center font-semibold rounded-xl transition-colors ${
                    tier.popular
                      ? 'bg-primary text-white hover:bg-primary/90'
                      : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                  }`}
                  data-testid={`tier-${tier.name.toLowerCase().replace(' ', '-')}-cta`}
                >
                  {tier.cta}
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* As-a-Service Modules */}
      <section className="py-12 sm:py-16 bg-slate-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-10">
            <span className="inline-block px-3 py-1 text-xs font-semibold text-primary bg-primary/10 rounded-full mb-3">
              À LA CARTE MODULES
            </span>
            <h2 className="text-2xl sm:text-3xl font-display font-bold mb-3">
              "As-a-Service" Add-Ons
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Enhance your management with our specialized services. Each module is optional and can be added to any tier.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-6">
            {asAServiceModules.map((module) => (
              <div key={module.id} className="bg-white rounded-xl p-6 border border-slate-200 hover:shadow-lg transition-shadow">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                    <module.icon className="w-5 h-5 text-primary" />
                  </div>
                  {module.popular && (
                    <span className="text-xs font-medium text-primary bg-primary/10 px-2 py-0.5 rounded-full">Popular</span>
                  )}
                </div>
                <h3 className="font-bold text-lg mb-1">{module.name}</h3>
                <div className="flex items-baseline gap-1 mb-2">
                  <span className="text-2xl font-bold text-primary">{module.price}</span>
                  <span className="text-sm text-slate-500">{module.priceDetail}</span>
                </div>
                <p className="text-sm text-slate-600 mb-4">{module.description}</p>
                <ul className="space-y-2">
                  {module.features.map((feature, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm text-slate-600">
                      <Check className="w-4 h-4 text-green-500 flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>
                {module.note && (
                  <p className="mt-4 text-xs text-slate-500 bg-slate-50 p-2 rounded">{module.note}</p>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Inspection Packages */}
      <section className="py-12 sm:py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-8">
            <h2 className="text-2xl sm:text-3xl font-display font-bold mb-3">
              Inspection Packages
            </h2>
            <p className="text-slate-600">Save with multi-inspection packs</p>
          </div>
          
          <div className="grid sm:grid-cols-3 gap-4">
            {inspectionPackages.map((pkg) => (
              <div 
                key={pkg.name} 
                className={`rounded-xl p-5 border-2 text-center ${pkg.recommended ? 'border-primary bg-primary/5' : 'border-slate-200'}`}
              >
                {pkg.recommended && (
                  <span className="inline-block text-xs font-semibold text-primary mb-2">RECOMMENDED</span>
                )}
                <h3 className="font-bold mb-1">{pkg.name}</h3>
                <p className="text-sm text-slate-500 mb-3">{pkg.inspections} inspection{pkg.inspections > 1 ? 's' : ''}</p>
                <div className="text-3xl font-bold text-primary mb-1">£{pkg.price}</div>
                <p className="text-sm text-slate-500">£{pkg.perInspection}/inspection</p>
                {pkg.savings && (
                  <p className="text-sm text-green-600 font-medium mt-2">Save £{pkg.savings}</p>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Additional Services */}
      <section className="py-12 sm:py-16 bg-slate-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <h2 className="text-2xl sm:text-3xl font-display font-bold text-center mb-8">
            Additional Protection
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {additionalServices.map((service) => (
              <div key={service.name} className="bg-white rounded-xl p-4 border border-slate-200">
                <h3 className="font-semibold text-sm mb-1">{service.name}</h3>
                <p className="text-primary font-bold mb-1">{service.price}</p>
                <p className="text-xs text-slate-500">{service.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Platform Fees */}
      <section className="py-12 sm:py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-8">
            <h2 className="text-2xl sm:text-3xl font-display font-bold mb-3">
              Platform Listing Fees
            </h2>
            <p className="text-slate-600 text-sm">
              These are pass-through costs - you choose where to advertise
            </p>
          </div>
          
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
            {platformFees.map((platform) => (
              <div key={platform.platform} className="text-center p-4 rounded-lg bg-slate-50">
                <p className="font-semibold text-sm">{platform.platform}</p>
                <p className="text-primary font-bold">{platform.price}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <FAQSection faqs={faqs} />

      <CTASection
        title="Ready to get started?"
        description="Join thousands of landlords enjoying transparent, modular pricing."
        primaryCTA={{ href: '/register', label: 'Get Started' }}
      />
    </PublicLayout>
  );
}
